import { handedness } from "utils/constants";
import {
  defaultHighlightsData,
  defaultLaunchData,
  initialSummaryData,
  summaryDataKeys,
} from "./constants";

export function getHighlightsTilesData(highlightsData: any) {
  if (!highlightsData) {
    return defaultHighlightsData;
  }

  return defaultHighlightsData.map((state: any) => {
    return {
      ...state,
      value: highlightsData[state?.key] ?? "--",
    };
  });
}

export function getLaunchTilesData(ball: any, club: any) {
  if (!ball || !club) {
    return defaultLaunchData;
  }

  return defaultLaunchData.map((state: any) => {
    return {
      ...state,
      value: ball[state?.key] ?? club[state?.key] ?? "--",
    };
  });
}

export function formatDeviceType(url: string): string {
  // Extract device type from URL
  if (url.includes("m2c-and")) {
    return "Android";
  } else if (url.includes("m2c-iOS")) {
    return "iOS";
  } else if (url.includes("dc1")) {
    return "Desktop";
  } else {
    return "--";
  }
}

export function formatDeviceModel(url: string): string {
  const words = url.split(" ");
  const keywordIndex = words.findIndex(
    (word) => word.toLowerCase() === "front" || word.toLowerCase() === "back",
  );

  // If a keyword is found, return the portion of the URL before it
  if (keywordIndex > -1) {
    return words.slice(0, keywordIndex).join(" ");
  }

  // Otherwise, return the original URL
  return url;
}

export const formatOrientation = (orientation: string): string => {
  // Capitalize the first letter of each word and replace "DownTheLine" with "Down the Line"
  return orientation
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
export const formatHandedness = (handString: any) => {
  // Check if the input string is "leftHanded" or "rightHanded"
  if (handString.toLowerCase() === "lefthanded") {
    return "Left Handed";
  } else if (handString.toLowerCase() === "righthanded") {
    return "Right Handed";
  } else {
    return handString; // Return the input string if it doesn't match the expected format
  }
};

export const formatString = (handString: any) => {
  // Capitalize the first letter of the string and return
  return handString.charAt(0).toUpperCase() + handString.slice(1);
};

export const formatSwingType = (swingType: string): string => {
  // Capitalize the first letter of each word and handle specific cases
  const swingTypeExceptions: any = {
    "full swing": "Full Swing",
    "short game": "Short Game",
    putter: "Putter",
    bunker: "Bunker",
    FaceOn: "Face On",
  };
  return (
    swingTypeExceptions[swingType.toLowerCase()] ||
    swingType.replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

export function getSummarydata(summaryData: any) {
  if (!summaryData) {
    return initialSummaryData;
  }

  return Object.keys(initialSummaryData).reduce((emptyObj: any, item: any) => {
    const key = summaryDataKeys[item] || "";
    let value = summaryData[key] || "--";

    if (key === "handedness" && value !== "--") {
      value = formatHandedness(value);
    }

    if (key === "orientation" && value !== "--") {
      value = formatOrientation(value);
    }

    if (key === "shutter" && value !== "--") {
      value = Number(value).toFixed(1).toString();
    }

    if (key === "iso" && value !== "--") {
      value = Number(value).toFixed(1).toString();
    }

    if (key === "clubType" && value !== "--") {
      value = formatString(value);
    }

    if (key === "swingType" && value !== "--") {
      value = formatSwingType(value);
    }

    if (item === "deviceType" && value !== "--") {
      value = formatDeviceType(value);
    }
    if (item === "mobileModel" && value !== "--") {
      value = formatDeviceModel(value);
    }

    if ((key === "duration" || key === "frame_rate") && value !== "--") {
      value = value.toFixed(2).toString();
    }

    return { ...emptyObj, [item]: value };
  }, {});
}

export const transformTimelineDataToChartData = (
  timelineData: any,
  type: any,
): any => {
  if (!timelineData) {
    return;
  }
  if (timelineData.message) {
    return;
  }

  let summary;
  if (type === "pose-preview") {
    summary = timelineData;
  } else {
    summary = timelineData.summary;
  }
  const {
    upload,
    wait_club,
    processing_club,
    visualize,
    processing,
    wait,
    total,
  } = summary;

  const uploadTime = upload ? upload.duration_sec : 0;
  const waitTimeClub = wait_club ? wait_club.duration_sec : 0;
  const clubProcessingTime = processing_club
    ? Number(processing_club.duration_sec)
    : 0;
  const processingTimePose = processing ? processing.duration_sec : 0;
  const waitTimePose = wait ? wait.duration_sec : 0;

  const visualizeTime = visualize ? visualize.duration_sec : 0;
  const totalTime = total ? total.duration_sec : 0;
  const hasVisualizeTime = visualizeTime !== 0; // Check if visualize time is not null or zero

  return [
    { name: "Upload Time", value: uploadTime },
    { name: "Pose Wait Time", value: waitTimePose },
    { name: "Pose Processing Time", value: processingTimePose },
    { name: "Club Wait Time", value: waitTimeClub },
    { name: "Club Processing Time", value: clubProcessingTime },
    { name: "Visualize Time", value: visualizeTime },
    { name: "Total Time", value: totalTime },
    // { name: "Current Status", value: hasVisualizeTime ? true : false },
  ];
};
export function getActiveUrlsBasedOnRoute({ pathname, sportName }: any) {
  let url1, url2, url3;

  switch (pathname) {
    case "/m2c-golf-history":
      url1 = "/get_positions";
      url2 = "/get_position_golf";
      url3 = "/get_tracer_golf";
      break;
    case "/m2c-launch-history":
      url1 = "/get_information_launch";
      url2 = "/get_position_launch";
      url3 = "/get_tracer_launch";
      break;
    case "/m2c-athletics-history":
      url1 = "/get_information_athletics";
      url2 = "/get_positions_athletics";
      url3 = "/get_athletics_pose_image";
      break;
    case "/user-history":
      switch (sportName) {
        case "Golf":
          url1 = "/get_positions";
          url2 = "/get_position_golf";
          url3 = "/get_tracer_golf";
          break;
        case "Athletics":
          url1 = "/get_information_athletics";
          url2 = "/get_positions_athletics";
          url3 = "/get_athletics_pose_image";
          break;
        default:
          break;
      }
      break;
    default:
      break;
  }
  return { url1, url2, url3 };
}

export function getMaxHeightForImage() {
  const innnerWidth = window.innerWidth;

  if (innnerWidth < 1600) {
    return "230px";
  }
  if (innnerWidth < 1700) {
    return "250px";
  }
  if (innnerWidth < 1800) {
    return "270px";
  }
  if (innnerWidth < 1900) {
    return "295px";
  }
  return "300px";
}

export function checkForNotRequired(position: any, swingDetail?: any) {
  const notRequired =
    swingDetail?.deviceType === "IOS" &&
    swingDetail?.orientation === "Down The Line" &&
    swingDetail?.swingType === "Full Swing";

  const positionToDisplay = ["P1", "P4", "P7", "P10"];

  return !positionToDisplay.includes(position) || notRequired
    ? "Not Required"
    : "";
}

export function getGridStyles(screens: any, containerWidth: any) {
  if (screens.xl) {
    return `repeat(2, ${containerWidth}) 1fr`;
  }

  if (screens.xl) {
    return `repeat(2, ${containerWidth})`;
  }

  return `repeat(2, 1fr)`;
}

export function calculateFixHeight({ screens }: any) {
  const innerwidth = window.innerWidth;
  const innerheight = window.innerHeight;

  if (screens.xxl || innerwidth > 1600) {
    return "calc(100dvh - 340px)";
  }

  return innerheight > 600 ? "52dvh" : "70dvh";
}
